import React, { useState, useEffect, useCallback } from 'react';
import Shimmer from 'sushiweb/Shimmer';
import './OrderTracker.scss';
import {
  MAP_EVENTS,
  sendReloadMapEvents,
} from '../../../helpers/analytics/map.analytics';
import { RenderMap, OrderRunMap } from './organism/atoms/Map/Map';
import { ErrorMapView } from './organism/atoms/ErrorMapView';

export const OrderTracker = ({
  orderId,
  userId,
  orderTrackInfo,
  getOrderTrackData,
  getOrderRunData,
  orderRunInfo,
  orderBasicInfo,
}) => {
  const isOrderLive = orderBasicInfo?.order?.is_live;
  if (isOrderLive) {
    return (
      <ETATracker
        orderId={orderId}
        userId={userId}
        orderTrackInfo={orderTrackInfo}
        getOrderTrackData={getOrderTrackData}
        orderBasicInfo={orderBasicInfo}
      />
    );
  }

  return (
    <OrderRun
      orderId={orderId}
      userId={userId}
      orderRunInfo={orderRunInfo}
      getOrderRunData={getOrderRunData}
      orderBasicInfo={orderBasicInfo}
    />
  );
};

const OrderRun = ({
  orderId,
  userId,
  orderRunInfo,
  getOrderRunData,
  orderBasicInfo,
}) => {
  const [showShimmer, setShowShimmer] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [mapError, setMapError] = useState(null);

  const makeCallToGetOrderRunData = useCallback(() => {
    let params = {
      order_id: orderId,
      user_id: userId,
    };
    return getOrderRunData(params)
      .then((res) => {
        setShowShimmer(false);
        if (res?.isError) {
          return setMapError(res);
        }
        if (res) {
          setMapError(false);
        }
      })
      .catch((err) => {
        setMapError(err);
      });
  }, [orderId, userId, getOrderRunData]);

  const refreshMap = useCallback(() => {
    setInitialLoad(false);
    sendReloadMapEvents(MAP_EVENTS.RELOAD_MAP_EVENT, orderId, userId);
    setShowShimmer(true);
    makeCallToGetOrderRunData();
  }, [orderId, userId, makeCallToGetOrderRunData]);

  let compToRender = (
    <OrderRunMap
      orderRunInfo={orderRunInfo}
      refreshMap={refreshMap}
      initialLoad={initialLoad}
      orderBasicInfo={orderBasicInfo}
    />
  );

  if (mapError) {
    compToRender = (
      <ErrorMapView
        message={mapError?.message || 'Something went wrong'}
        refreshMap={refreshMap}
      />
    );
  }

  return (
    <div className='orderTrackerContainer row'>
      {showShimmer ? (
        <Shimmer height='20rem' width='100%' />
      ) : (
        <div className='mapContainer col'>
          <div className='mapBox col'>{compToRender}</div>
        </div>
      )}
    </div>
  );
};

const ETATracker = ({
  orderId,
  userId,
  orderTrackInfo,
  getOrderTrackData,
  orderBasicInfo,
}) => {
  const [showShimmer, setShowShimmer] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const [lastUpdatedAt, setLastUpdatedAt] = useState(0);
  const [mapError, setMapError] = useState(null);

  const makeCallToGetOrderTrackData = useCallback(() => {
    let params = {
      order_id: orderId,
      user_id: userId,
    };
    return getOrderTrackData(params)
      .then((res) => {
        setShowShimmer(false);
        if (res?.isError) {
          return setMapError(res);
        }
        if (res) {
          setMapError(false);
          setLastUpdatedAt(Date.now());
        }
      })
      .catch((err) => {
        setMapError(err);
      });
  }, [orderId, userId, getOrderTrackData]);

  useEffect(() => {
    makeCallToGetOrderTrackData();
  }, []);

  const refreshMap = useCallback(() => {
    setInitialLoad(false);
    sendReloadMapEvents(MAP_EVENTS.RELOAD_MAP_EVENT, orderId, userId);
    setShowShimmer(true);
    makeCallToGetOrderTrackData();
  }, [orderId, userId, makeCallToGetOrderTrackData]);

  let compToRender = (
    <>
      {orderTrackInfo?.rider_status && (
        <div
          css={`
            background-color: #256fef;
            padding: 0.5rem 0.8rem;
            color: white;
            font-weight: 500;
            font-size: 1.2rem;
            border-radius: 0.4rem 0.4rem 0px 0px;
          `}
        >
          {orderTrackInfo?.rider_status}
        </div>
      )}
      <RenderMap
        mapData={orderTrackInfo}
        orderId={orderId}
        refreshMap={refreshMap}
        lastUpdatedAt={lastUpdatedAt}
        initialLoad={initialLoad}
        orderBasicInfo={orderBasicInfo}
      />
    </>
  );

  if (mapError) {
    compToRender = (
      <ErrorMapView
        message={mapError?.message || 'Something went wrong'}
        refreshMap={refreshMap}
      />
    );
  }

  return (
    <div className='orderTrackerContainer row'>
      {showShimmer ? (
        <Shimmer height='20rem' width='100%' />
      ) : (
        <div className='mapContainer col'>
          <div className='mapBox col'>{compToRender}</div>
        </div>
      )}
    </div>
  );
};
